import { useState } from "react";
import { Button } from "@volca/ui/components/ui/button";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import {
  TimeRangePicker,
  type TimeRange,
} from "@volca/ui/components/ui/time-range-picker";
import { useBreakpoint } from "@volca/ui/hooks/use-breakpoint";
import { toast } from "@volca/ui/lib/toast";
import { REPORT_PERIOD_PRESETS } from "./constants";
import { useCreateReport } from "./query";
import type { ReportDates } from "./types";
import { dateRangeError, reportError, toIsoDate } from "./utils";

const toDates = (range: TimeRange | null): ReportDates | null =>
  range?.from && range.to
    ? { start_date: toIsoDate(range.from), end_date: toIsoDate(range.to) }
    : null;

/**
 * The page header's right side for ServiceTitan reports: pick a period, run
 * it. The report reads ServiceTitan for that period, so there is nothing to
 * upload; documents belong to the templates of documents-only Job Costing.
 */
export const NewReportControls = ({
  businessId,
  onCreated,
}: {
  businessId: string;
  onCreated: (runId: string) => void;
}) => {
  const [range, setRange] = useState<TimeRange | null>(null);
  const create = useCreateReport(businessId, 1);
  const dates = toDates(range);
  const validation = dates ? dateRangeError(dates) : null;
  const isMobile = useBreakpoint() === "mobile";

  return (
    <Stack direction="column" align="end" gap={1} className="w-full lg:w-auto">
      <Stack
        direction="row"
        align="center"
        justify="end"
        gap={2}
        className="w-full flex-wrap gap-y-2 lg:w-auto"
      >
        {/* On a phone the picker takes the row's spare width beside the button. */}
        <Stack align="start" gap={0} className="min-w-0 flex-1 md:flex-none">
          <TimeRangePicker
            placeholder="Select a report period"
            presets={REPORT_PERIOD_PRESETS}
            value={range}
            onChange={setRange}
            // A profitability report is worth running for any period, including
            // one no preset covers. Without typing, reaching an old quarter means
            // clicking the calendar back a month at a time.
            allowTextEntry
            fullWidth={isMobile}
          />
        </Stack>
        <Button
          disabled={!dates || !!validation || create.isPending}
          aria-busy={create.isPending}
          onClick={() => {
            if (!dates || validation) return;
            // An empty list, not none: a missing one makes the API carry
            // forward whatever documents the last run of these dates read.
            create.mutate(
              { ...dates, document_ids: [] },
              {
                onSuccess: (run) => onCreated(run.id),
                onError: (error) =>
                  toast.error("The report could not be started", {
                    description: reportError(error),
                  }),
              },
            );
          }}
        >
          Run report
        </Button>
      </Stack>
      {/* A disabled button with no reason beside it is just a dead control. */}
      {validation && <Text className="text-danger">{validation}</Text>}
    </Stack>
  );
};
